<template>
  <article class="feed-item">
    <div class="feed-head">
      <el-avatar
        :src="post.authorAvatar"
        :size="40"
        class="feed-avatar"
        @click="$emit('open-profile', post.authorId)"
      >
        {{ (post.authorName || "?").charAt(0) }}
      </el-avatar>
      <div class="feed-meta">
        <div class="feed-author" @click="$emit('open-profile', post.authorId)">
          {{ post.authorName || "用户" }}
        </div>
        <div class="feed-time">{{ formatTime(post.createTime) }}</div>
      </div>
    </div>

    <div class="feed-content">
      <p class="feed-text" :class="{ 'is-clamped': !expanded && needClamp }">
        {{ displayText }}
      </p>
      <button
        v-if="needClamp"
        type="button"
        class="feed-expand"
        @click="expanded = !expanded"
      >
        {{ expanded ? "收起" : "展开" }}
      </button>
    </div>

    <div v-if="previewImages.length" class="feed-images" :class="imageGridClass">
      <el-image
        v-for="(url, idx) in previewImages"
        :key="url + idx"
        :src="url"
        :preview-src-list="allImages"
        :initial-index="idx"
        fit="cover"
        class="feed-image"
        preview-teleported
      />
    </div>

    <div v-if="postTopics.length" class="feed-topic">
      <el-tag
        v-for="topic in postTopics"
        :key="topic.id"
        size="small"
        effect="plain"
        type="primary"
        class="feed-topic-tag"
        @click="$emit('select-topic', topic.id)"
      >
        {{ topic.name }}
      </el-tag>
    </div>

    <div class="feed-actions">
      <button
        type="button"
        class="feed-action"
        :class="{ 'is-active': isLiked, 'is-disabled': !likeClickable }"
        :disabled="!likeClickable"
        @click="toggleLike"
      >
        <el-icon><IconLike /></el-icon>
        <span>{{ localLikeCount }}</span>
      </button>
      <button
        type="button"
        class="feed-action"
        :class="{ 'is-active': commentsOpen }"
        @click="toggleComments"
      >
        <el-icon><ChatDotRound /></el-icon>
        <span>{{ localCommentCount }}</span>
      </button>
      <button
        type="button"
        class="feed-action"
        :class="{ 'is-active': isFavorited, 'is-disabled': !favoriteClickable }"
        :disabled="!favoriteClickable"
        @click="toggleFavorite"
      >
        <el-icon><Star /></el-icon>
        <span>{{ localFavoriteCount }}</span>
      </button>

      <el-dropdown
        v-if="canDelete"
        trigger="click"
        class="feed-action-more"
        @command="handleMoreCommand"
      >
        <button type="button" class="feed-action feed-action-more-btn" aria-label="更多">
          <el-icon :size="16"><IconMore /></el-icon>
        </button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="delete" :disabled="deleteLoading"
              >删除</el-dropdown-item
            >
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <div
      v-if="commentsOpen"
      class="feed-comments"
      :class="{ 'is-empty': !hasCommentList }"
    >
      <div v-loading="commentsLoading">
        <u-comment :config="commentConfig" @submit="handleSubmitComment">
          <template #avatar="slotProps">
            <el-avatar
              :src="
                getCommentUser(slotProps)?.avatar ||
                getCommentUser(slotProps)?.user?.avatar
              "
              :size="28"
              @click.stop="
                $emit(
                  'open-profile',
                  getCommentUser(slotProps)?.uid || getCommentUser(slotProps)?.user?.uid
                )
              "
            >
              {{
                (
                  getCommentUser(slotProps)?.username ||
                  getCommentUser(slotProps)?.user?.username ||
                  "?"
                ).charAt(0)
              }}
            </el-avatar>
          </template>
        </u-comment>
      </div>
    </div>
  </article>
</template>

<script>
import { ChatDotRound, Star } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { UComment } from "undraw-ui";
import IconLike from "@/components/icons/IconLike.vue";
import IconMore from "@/components/icons/IconMore.vue";
import { formatTime } from "@/utils/format";
import { likePost, unlikePost } from "@/api/postLike";
import { favoritePost, unfavoritePost } from "@/api/postFavorite";
import { getPostComments, publishComment } from "@/api/postComment";
import { deletePost } from "@/api/post";
import { getToken } from "@/utils/authStorage";

const PREVIEW_LEN = 160;

export default {
  name: "PostFeedItem",
  components: { ChatDotRound, Star, IconLike, IconMore, UComment },
  inject: {
    layoutRef: { default: null },
  },
  props: {
    post: { type: Object, required: true },
    /** 详情弹窗：全文展示并默认展开评论 */
    detailMode: { type: Boolean, default: false },
    /** 是否允许点击点赞（收藏列表页可关掉） */
    likeClickable: { type: Boolean, default: true },
    /** 是否允许点击收藏（点赞列表页可关掉） */
    favoriteClickable: { type: Boolean, default: true },
  },
  emits: [
    "need-login",
    "open-profile",
    "select-topic",
    "comment-count-change",
    "deleted",
    "unliked",
    "unfavorited",
  ],
  data() {
    return {
      expanded: false,
      commentsOpen: false,
      commentsLoading: false,
      commentsLoaded: false,
      isLiked: !!this.post.liked,
      likeLoading: false,
      isFavorited: !!this.post.favorited,
      favoriteLoading: false,
      deleteLoading: false,
      localLikeCount: this.post.likeCount ?? 0,
      localCommentCount: this.post.commentCount ?? 0,
      localFavoriteCount: this.post.favoriteCount ?? 0,
      commentConfig: {
        user: { id: "guest", username: "游客", avatar: "" },
        comments: [],
        show: {
          form: true,
          content: true,
          level: false,
          likes: false,
          address: false,
          homeLink: false,
          reply: true,
        },
        placeholder: "说点什么...",
      },
    };
  },
  computed: {
    rawText() {
      return (this.post.content || "").trim();
    },
    postTopics() {
      return Array.isArray(this.post.topics) ? this.post.topics : [];
    },
    needClamp() {
      if (this.detailMode) return false;
      return this.rawText.length > PREVIEW_LEN;
    },
    displayText() {
      if (this.expanded || !this.needClamp) return this.rawText;
      return this.rawText.slice(0, PREVIEW_LEN) + "…";
    },
    allImages() {
      const list = Array.isArray(this.post.images)
        ? this.post.images.filter(Boolean)
        : [];
      return list;
    },
    previewImages() {
      return this.detailMode ? this.allImages : this.allImages.slice(0, 4);
    },
    imageGridClass() {
      const n = this.previewImages.length;
      if (n <= 1) return "is-one";
      if (n === 2) return "is-two";
      if (n === 3) return "is-three";
      return "is-four";
    },
    currentUser() {
      if (!getToken()) return null;
      const u = this.layoutRef?.userInfo;
      return u?.id != null ? u : null;
    },
    canDelete() {
      const uid = this.currentUser?.id;
      return uid != null && Number(uid) === Number(this.post.authorId);
    },
    hasCommentList() {
      return (this.commentConfig.comments || []).length > 0;
    },
  },
  watch: {
    currentUser: {
      handler(u) {
        if (u) {
          this.commentConfig.user = {
            id: String(u.id),
            username: u.nickName || u.username || "用户",
            avatar: u.avatar || "",
          };
          this.commentConfig.placeholder = "说点什么...";
        } else {
          this.commentConfig.user = { id: "guest", username: "游客", avatar: "" };
          this.commentConfig.placeholder = "请先登录后发表评论";
        }
      },
      immediate: true,
      deep: true,
    },
    "post.likeCount"(v) {
      this.localLikeCount = v ?? 0;
    },
    "post.commentCount"(v) {
      this.localCommentCount = v ?? 0;
    },
    "post.favoriteCount"(v) {
      this.localFavoriteCount = v ?? 0;
    },
    "post.liked"(v) {
      this.isLiked = !!v;
    },
    "post.favorited"(v) {
      this.isFavorited = !!v;
    },
  },
  mounted() {
    if (this.detailMode) {
      this.commentsOpen = true;
      this.loadComments();
    }
  },
  methods: {
    formatTime,
    getCommentUser(slotProps) {
      return slotProps?.item || slotProps || {};
    },
    async toggleLike() {
      if (!this.likeClickable) return;
      if (!getToken()) {
        this.$emit("need-login");
        return;
      }
      if (this.likeLoading) return;
      this.likeLoading = true;
      try {
        if (this.isLiked) {
          const res = await unlikePost(this.post.id);
          if (res?.data?.code === 200) {
            this.isLiked = false;
            this.localLikeCount = Math.max(0, this.localLikeCount - 1);
            this.$emit("unliked", this.post.id);
          }
        } else {
          const res = await likePost(this.post.id);
          if (res?.data?.code === 200) {
            this.isLiked = true;
            this.localLikeCount += 1;
          }
        }
      } catch (e) {
        ElMessage.error("操作失败");
      } finally {
        this.likeLoading = false;
      }
    },
    async toggleFavorite() {
      if (!this.favoriteClickable) return;
      if (!getToken()) {
        this.$emit("need-login");
        return;
      }
      if (this.favoriteLoading) return;
      this.favoriteLoading = true;
      try {
        if (this.isFavorited) {
          const res = await unfavoritePost(this.post.id);
          if (res?.data?.code === 200) {
            this.isFavorited = false;
            this.localFavoriteCount = Math.max(0, this.localFavoriteCount - 1);
            this.$emit("unfavorited", this.post.id);
          }
        } else {
          const res = await favoritePost(this.post.id);
          if (res?.data?.code === 200) {
            this.isFavorited = true;
            this.localFavoriteCount += 1;
          }
        }
      } catch (e) {
        ElMessage.error("操作失败");
      } finally {
        this.favoriteLoading = false;
      }
    },
    handleMoreCommand(command) {
      if (command === "delete") this.handleDelete();
    },
    async handleDelete() {
      if (!this.canDelete || this.deleteLoading) return;
      try {
        await ElMessageBox.confirm("确定要删除这篇帖子吗？", "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        });
      } catch {
        return;
      }
      this.deleteLoading = true;
      try {
        const res = await deletePost(this.post.id);
        if (res?.data?.code === 200) {
          ElMessage.success("删除成功");
          this.$emit("deleted", this.post.id);
        } else {
          ElMessage.error(res?.data?.msg || "删除失败");
        }
      } catch (e) {
        ElMessage.error("删除失败");
      } finally {
        this.deleteLoading = false;
      }
    },
    async toggleComments() {
      this.commentsOpen = !this.commentsOpen;
      if (this.commentsOpen && !this.commentsLoaded) {
        await this.loadComments();
      }
    },
    async loadComments() {
      this.commentsLoading = true;
      try {
        const res = await getPostComments(this.post.id);
        if (res?.data?.code === 200) {
          this.commentConfig.comments = res.data.data || [];
          this.commentsLoaded = true;
        }
      } catch (e) {
        ElMessage.error("加载评论失败");
      } finally {
        this.commentsLoading = false;
      }
    },
    async handleSubmitComment(submitParam) {
      if (!getToken()) {
        submitParam?.finish?.();
        this.$emit("need-login");
        return;
      }
      try {
        const res = await publishComment(
          this.post.id,
          submitParam.content,
          submitParam.parentId ? Number(submitParam.parentId) : null
        );
        if (res?.data?.code === 200) {
          ElMessage.success(submitParam.parentId ? "回复成功" : "评论成功");
          this.localCommentCount += 1;
          this.$emit("comment-count-change", this.localCommentCount);
          // 先 finish 清空输入框；若先 reload 会卸载 u-comment，导致 clear 空引用
          try {
            submitParam?.finish?.();
          } catch (e) {
            console.warn("评论输入框清理失败:", e);
          }
          await this.loadComments();
        } else {
          ElMessage.error(res?.data?.msg || "评论失败");
          try {
            submitParam?.finish?.();
          } catch (e) {
            /* ignore */
          }
        }
      } catch (e) {
        ElMessage.error("评论失败");
        try {
          submitParam?.finish?.();
        } catch (err) {
          /* ignore */
        }
      }
    },
  },
};
</script>

<style scoped>
.feed-item {
  background: var(--el-bg-color);
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 12px;
}

.feed-head {
  display: flex;
  gap: 12px;
  align-items: center;
}

.feed-avatar {
  cursor: pointer;
  flex-shrink: 0;
}

.feed-meta {
  min-width: 0;
}

.feed-author {
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  color: var(--el-text-color-primary);
}

.feed-author:hover {
  color: var(--el-color-primary);
}

.feed-time {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin-top: 2px;
}

.feed-content {
  margin-top: 12px;
}

.feed-text {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.7;
  font-size: 15px;
  color: var(--el-text-color-primary);
}

.feed-expand {
  margin-top: 6px;
  border: none;
  background: transparent;
  color: var(--el-color-primary);
  cursor: pointer;
  padding: 0;
  font-size: 14px;
}

.feed-images {
  display: grid;
  gap: 6px;
  margin-top: 12px;
  max-width: 420px;
}

.feed-images.is-one {
  grid-template-columns: 1fr;
  max-width: 280px;
}

.feed-images.is-two,
.feed-images.is-four {
  grid-template-columns: 1fr 1fr;
}

.feed-images.is-three {
  grid-template-columns: 1fr 1fr;
}

.feed-image {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
}

.feed-images.is-one .feed-image {
  aspect-ratio: 4 / 3;
}

.feed-topic {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.feed-topic-tag {
  cursor: pointer;
}

.feed-actions {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--el-border-color-extra-light);
}

.feed-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: var(--el-text-color-secondary);
  cursor: pointer;
  font-size: 13px;
  padding: 4px 0;
}

.feed-action:hover,
.feed-action.is-active {
  color: var(--el-color-primary);
}

.feed-action.is-disabled,
.feed-action.is-disabled:hover {
  cursor: default;
  color: var(--el-text-color-secondary);
  opacity: 0.85;
}

.feed-action.is-disabled.is-active,
.feed-action.is-disabled.is-active:hover {
  color: var(--el-color-primary);
  opacity: 1;
}

.feed-action-more {
  margin-left: auto;
  line-height: 1;
}

.feed-action-more-btn {
  padding: 4px;
}

.feed-comments {
  margin-top: 12px;
  padding-top: 4px;
}

.feed-comments.is-empty :deep(.comment-list-wrapper) {
  display: none;
}
</style>
